import { requireService, wrap } from '@repo/di-ez';
import { hasValue } from '@repo/utils';
import * as echarts from 'echarts';
import 'echarts-gl';
import { useEffect, useRef } from 'react';

import { TrajectoryChartWarnings } from '../trajectory-chart-warnings';
import type { Trajectory3dLine, Trajectory3dViewControlState } from '../trajectory-graph.store';
import { ceilToInterval, computePlanCartesianRanges, niceGridInterval } from '../utils/chart-axis-layout';
import { formatGridLabel } from '../utils/format-chart-number';

import styles from './trajectory-3d-chart.module.scss';
import {
  disableNativeLine3DPicking,
  findNearbyTrajectoryLineHits,
  formatTrajectory3dTooltipHtml,
  showAxisPointerForClosestHit,
} from './trajectory-3d-multi-tooltip';

const CHART_COLOR = '#ffffff';
const CHART_COLOR_MUTED = 'rgba(255, 255, 255, 0.35)';
const VIEW_CONTROL_ROTATE_SENSITIVITY = 5;
/**
 * Diameter (in screen pixels) of the virtual hit circle around the cursor used to
 * detect nearby trajectory lines for the tooltip. Intentionally decoupled from the
 * rendered line width: it exists purely so the user doesn't need to aim precisely
 * at a thin line, and all lines whose on-screen projection falls inside this
 * circle are reported (not just the closest one), since several curves can look
 * close together on screen depending on the camera angle even if they are far
 * apart in 3D space.
 */
const TOOLTIP_HIT_DIAMETER = 24;
/** Floor for |WE| / |NS| so a near-vertical well still has a readable plan face. */
const MIN_HORIZONTAL_HALF = 500;

const axis3DStyle = {
  nameTextStyle: { color: CHART_COLOR },
  axisLine: { lineStyle: { color: CHART_COLOR } },
  axisTick: { lineStyle: { color: CHART_COLOR } },
  axisLabel: { color: CHART_COLOR },
  splitLine: { lineStyle: { color: CHART_COLOR_MUTED } },
};

function extractViewControl(chart: echarts.ECharts): Trajectory3dViewControlState | null {
  const option = chart.getOption() as { grid3D?: Array<{ viewControl?: Trajectory3dViewControlState }> };
  const viewControl = option.grid3D?.[0]?.viewControl;

  if (!viewControl) {
    return null;
  }

  const { alpha, beta, distance, center } = viewControl;

  return { alpha, beta, distance, center };
}

/**
 * WE/NS come from trajectory data only (max |value| per axis). The plan face is
 * at least ±500; the grid step grows with the larger axis. TVD is independent
 * and is never used to inflate WE/NS values. The visual box stays a cube —
 * numeric spans may differ.
 *
 * Corner dummy points set the data extent. dataMin/dataMax keep echarts-gl from
 * nicening ±500 up to ±600. Numeric min/max made line3D disappear.
 */
function compute3dAxisLayout(lines: Trajectory3dLine[]): {
  weMin: number;
  weMax: number;
  nsMin: number;
  nsMax: number;
  tvdMin: number;
  tvdMax: number;
  horizontalInterval: number;
  tvdInterval: number;
  boxWidth: number;
  boxDepth: number;
  boxHeight: number;
} {
  const allWe = lines.flatMap((line) => line.data.map(([we]) => we));
  const allNs = lines.flatMap((line) => line.data.map(([, ns]) => ns));
  const allTvdDepth = lines.flatMap((line) => line.data.map(([, , z]) => -z));

  const { x: we, y: ns } = computePlanCartesianRanges(allWe, allNs, MIN_HORIZONTAL_HALF);

  const tvdRawSpan = allTvdDepth.length ? Math.max(...allTvdDepth, 0) : 1;
  const tvdInterval = niceGridInterval(Math.max(tvdRawSpan, 1));
  const tvdSpan = Math.max(ceilToInterval(Math.max(tvdRawSpan, 1), tvdInterval), tvdInterval);

  const boxScale = 100;

  return {
    weMin: we.min,
    weMax: we.max,
    nsMin: ns.min,
    nsMax: ns.max,
    tvdMin: -tvdSpan,
    tvdMax: 0,
    horizontalInterval: we.interval,
    tvdInterval,
    boxWidth: boxScale,
    boxDepth: boxScale,
    boxHeight: boxScale,
  };
}

function buildChartOption(
  lines: Trajectory3dLine[],
  savedViewControl: Trajectory3dViewControlState | null,
): echarts.EChartsOption {
  const layout = compute3dAxisLayout(lines);

  const series = [
    ...lines.map((line) => ({
      type: 'line3D' as const,
      name: `Trajectory_${line.label}`,
      data: line.data,
      shading: 'color' as const,
      silent: true,
      lineStyle: {
        width: line.lineWidth,
        color: line.color,
      },
      itemStyle: {
        color: line.color,
      },
    })),
    {
      type: 'scatter3D' as const,
      name: '__axisExtent',
      data: [
        [layout.weMin, layout.nsMin, layout.tvdMin],
        [layout.weMax, layout.nsMax, layout.tvdMax],
      ],
      symbolSize: 0,
      silent: true,
      itemStyle: { opacity: 0 },
    },
  ];

  return {
    animation: false,
    textStyle: {
      color: CHART_COLOR,
    },
    tooltip: {
      show: false,
    },
    grid3D: {
      boxWidth: layout.boxWidth,
      boxDepth: layout.boxDepth,
      boxHeight: layout.boxHeight,
      viewControl: {
        projection: 'perspective',
        autoRotate: false,
        distance: savedViewControl?.distance ?? 200,
        ...(savedViewControl?.alpha !== undefined ? { alpha: savedViewControl.alpha } : {}),
        ...(savedViewControl?.beta !== undefined ? { beta: savedViewControl.beta } : {}),
        rotateSensitivity: VIEW_CONTROL_ROTATE_SENSITIVITY,
      },
      axisLine: {
        lineStyle: {
          color: CHART_COLOR,
        },
      },
      axisPointer: {
        show: false,
        lineStyle: {
          color: CHART_COLOR,
        },
      },
      splitLine: {
        lineStyle: {
          color: CHART_COLOR_MUTED,
        },
      },
      light: {
        main: {
          intensity: 1.2,
        },
        ambient: {
          intensity: 0.4,
        },
      },
    },
    xAxis3D: {
      type: 'value',
      name: 'WE',
      min: 'dataMin',
      max: 'dataMax',
      interval: layout.horizontalInterval,
      ...axis3DStyle,
      axisLabel: {
        ...axis3DStyle.axisLabel,
        formatter: (value: number) => formatGridLabel(value),
      },
      axisPointer: { show: true },
    },
    yAxis3D: {
      type: 'value',
      name: 'NS',
      min: 'dataMin',
      max: 'dataMax',
      interval: layout.horizontalInterval,
      ...axis3DStyle,
      axisLabel: {
        ...axis3DStyle.axisLabel,
        formatter: (value: number) => formatGridLabel(value),
      },
      axisPointer: { show: true },
    },
    zAxis3D: {
      type: 'value',
      name: 'TVD',
      interval: layout.tvdInterval,
      ...axis3DStyle,
      axisLabel: {
        ...axis3DStyle.axisLabel,
        formatter: (value: number) => formatGridLabel(-value),
      },
      axisPointer: { show: true },
    },
    series,
  } as echarts.EChartsOption;
}

export const Trajectory3dChart = wrap(function Trajectory3dChart() {
  const graphStore = requireService('trajectoryGraphStore');
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const isChartInitializedRef = useRef(false);
  const linesRef = useRef(graphStore.mainTrajectory3dLines);

  const chartLines = graphStore.mainTrajectory3dLines;
  linesRef.current = chartLines;

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const chart = echarts.init(container);
    const tooltip = document.createElement('div');
    tooltip.className = `${styles.tooltip} caption`;
    tooltip.style.display = 'none';
    container.appendChild(tooltip);
    chartRef.current = chart;
    tooltipRef.current = tooltip;
    graphStore.registerChartInstance(chart);
    chart.setOption(buildChartOption(linesRef.current, graphStore.trajectory3dViewControl), true);
    isChartInitializedRef.current = true;
    disableNativeLine3DPicking(chart);

    const hideTooltip = () => {
      tooltip.style.display = 'none';
      chart.dispatchAction({ type: 'grid3DHideAxisPointer' });
    };

    const handleMouseMove = (event: { offsetX?: number; offsetY?: number }) => {
      const mouseX = event.offsetX;
      const mouseY = event.offsetY;

      if (!hasValue(mouseX) || !hasValue(mouseY)) {
        hideTooltip();
        return;
      }

      const hits = findNearbyTrajectoryLineHits(chart, linesRef.current, mouseX, mouseY, TOOLTIP_HIT_DIAMETER / 2);

      if (!hits.length) {
        hideTooltip();
        return;
      }

      tooltip.innerHTML = formatTrajectory3dTooltipHtml(hits);
      tooltip.style.display = 'block';
      tooltip.style.left = `${mouseX + 12}px`;
      tooltip.style.top = `${mouseY + 12}px`;

      showAxisPointerForClosestHit(chart, hits);
    };

    const persistViewControl = () => {
      const viewControl = extractViewControl(chart);

      if (viewControl) {
        graphStore.setTrajectory3dViewControl(viewControl);
      }
    };

    const zr = chart.getZr();
    zr.on('mousemove', handleMouseMove);
    zr.on('globalout', hideTooltip);
    zr.on('mouseup', persistViewControl);
    zr.on('mousewheel', persistViewControl);

    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
      disableNativeLine3DPicking(chart);
    });

    resizeObserver.observe(container);

    return () => {
      persistViewControl();
      zr.off('mousemove', handleMouseMove);
      zr.off('globalout', hideTooltip);
      zr.off('mouseup', persistViewControl);
      zr.off('mousewheel', persistViewControl);
      resizeObserver.disconnect();
      tooltip.remove();
      tooltipRef.current = null;
      graphStore.unregisterChartInstance(chart);
      chart.dispose();
      chartRef.current = null;
      isChartInitializedRef.current = false;
    };
  }, [graphStore]);

  useEffect(() => {
    const chart = chartRef.current;

    if (!chart || !isChartInitializedRef.current) {
      return;
    }

    chart.setOption(buildChartOption(chartLines, graphStore.trajectory3dViewControl), true);
    disableNativeLine3DPicking(chart);
  }, [chartLines, graphStore]);

  return (
    <div className={styles.root}>
      <TrajectoryChartWarnings warnings={graphStore.excluded3dWarnings} />
      <div ref={containerRef} className={styles.chart} />
    </div>
  );
});
